import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';

import { MobileSidebarService } from '@app/core/services/mobile-sidebar.service';
import { LayoutService } from '@app/layout/service/layout.service';
import { SystemContextService } from '@app/core/services/system-context.service';
import { AuthService } from '@app/core/auth/auth.service';
import { SistemaDatosService } from '@app/core/services/sistema-datos.service';
import { UserI } from '@app/interfaces/user.interface';
import { environment } from 'src/environments/environment';
import { Usuario } from '@app/models/usuario';

@Component({
    selector: 'app-mobile-sidebar',
    templateUrl: './app-mobile-sidebar.component.html',
    styleUrls: ['./app-mobile-sidebar.component.scss'],
    standalone: false
})
export class AppMobileSidebarComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private hasApiProfile = false;

    // Datos para la cabecera y el usuario (como en welcome card)
    username: string = '';
    avatarUrl?: string;

    items: MenuItem[] = [];
    private currentUserFull: Usuario | null = null;

    constructor(
        public mobileSidebar: MobileSidebarService,
        private router: Router,
        private host: ElementRef<HTMLElement>,
        public layoutService: LayoutService,
        private systemContext: SystemContextService,
        private auth: AuthService,
        private cdr: ChangeDetectorRef,
        private sistemaSvc: SistemaDatosService
    ) {}

    ngOnInit(): void {
        // 1) Stream de usuario actual (rápido, puede ser parcial). Si ya tenemos perfil del API, no sobreescribimos.
        this.auth.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((u: Usuario | null) => {
            if (!this.hasApiProfile) {
                const candidateName = (u?.nombre ?? u?.usuario ?? '').toString();
                if (candidateName) this.username = candidateName;

                const candidateAvatar = this.pickAvatar(u) ?? this.pickAvatar(this.readUserFromStorage()) ?? undefined;
                if (candidateAvatar && !this.isAbsoluteUrl(this.avatarUrl)) {
                    this.avatarUrl = candidateAvatar;
                }
            }
            // Guardar usuario completo y reconstruir menú con permisos actualizados
            this.currentUserFull = u;
            this.buildMenu();
            this.cdr.markForCheck();
        });

        // 2) Carga una vez desde API (mismo patrón que Welcome Card)
        this.loadFromApiOnce();

        this.buildMenu();

        // Cargar branding textual (título/subtítulo/pais)
        this.sistemaSvc.get().subscribe({
            next: (d) => {
                if (d) {
                    this.sdTitle = (d.titulo ?? 'CN Sapag SIGD').toString();
                    this.sdSubtitle = (d.subtitulo ?? 'CN Sapag Sistema Integral de Gestión Digital').toString();
                    this.sdPais = (d.pais ?? 'Argentina').toString();
                    this.sdTopbarLight = d.logo_topbar || undefined;
                    this.sdTopbarDark = d.logo_topbar_dark || undefined;
                    this.cdr.markForCheck();
                }
            },
            error: () => {}
        });
        // Fallback local desde settings si BD no trae campos
        try {
            const raw = localStorage.getItem('system_settings_administracion-blister');
            if (raw) {
                const obj = JSON.parse(raw);
                this.sdTitle = obj.systemTitle || this.sdTitle;
                this.sdSubtitle = obj.systemSubtitle || this.sdSubtitle;
                this.sdPais = obj.country || this.sdPais;
                this.sdTopbarLight = obj.topbarIconUrlLight || this.sdTopbarLight;
                this.sdTopbarDark = obj.topbarIconUrlDark || this.sdTopbarDark;
            }
        } catch {}
    }

    /** Lee el user persistido por AuthService si hace falta */
    private readUserFromStorage(): any | null {
        try {
            const raw = localStorage.getItem('cf_current_user_v1');
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    /** Extrae un avatar mirando varios nombres comunes */
    private pickAvatar(src: any | null | undefined): string | undefined {
        if (!src || typeof src !== 'object') return undefined;
        const candidates = ['avatarUrl', 'avatar', 'fotoUrl', 'foto', 'photoURL', 'imageUrl', 'imagen', 'imagen_perfil', 'picture', 'profileImage', 'profile_image'];
        for (const key of candidates) {
            const v = src[key];
            if (typeof v === 'string' && v.trim()) return v.trim();
        }
        return undefined;
    }

    private isAbsoluteUrl(u?: string): boolean {
        if (!u) return false;
        return /^(https?:)?\/\//.test(u) || u.startsWith('data:');
    }

    /** Carga una sola vez los datos completos (imagen_perfil + nombre) desde el API */
    private async loadFromApiOnce() {
        try {
            const usuario = await this.auth.returnUserInfo();
            if (usuario) {
                // Avatar según welcome card
                if (usuario.imagen_perfil) {
                    this.avatarUrl = `${environment.imagesUrl}avatar/${usuario.imagen_perfil}`;
                }

                // Nombre preferente: Nombre (o usuario)
                const preferred = [usuario.nombre]
                    .filter((p): p is string => !!p && p.trim().length > 0)
                    .join(' ')
                    .trim();
                this.username = (preferred || usuario.usuario || '').trim();

                this.hasApiProfile = true;
                this.cdr.markForCheck();
            }
        } catch {
            // silencioso; quedamos con el stream
        }
    }

    // ⬇️ lo demás igual (displayFormattedName, resolvedAvatarUrl, initials, etc.)
    get resolvedAvatarUrl(): string | undefined {
        const p = this.avatarUrl?.trim();
        if (!p) return undefined;
        if (p.startsWith('http') || p.startsWith('data:') || p.startsWith('assets/')) return p;
        // Era incorrecto: 'assets/images/users/' → usar la URL del servidor
        return `${environment.imagesUrl}avatar/${p}`;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Nombre formateado
    get displayFormattedName(): string {
        const name = (this.username ?? '').trim();
        if (!name) return 'Usuario';
        return name.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    }

    // Iniciales
    get initials(): string {
        const parts = this.displayFormattedName.split(' ').filter(Boolean);
        return (parts[0]?.[0] ?? 'U') + (parts[1]?.[0] ?? '');
    }

    // Logo igual al Topbar (dinámico por sistema-datos con fallback a assets)
    sdTopbarLight?: string;
    sdTopbarDark?: string;
    get logoSrc(): string {
        const light = this.sdTopbarLight || 'assets/images/icons/cn-sapag-logo.png';
        const dark = this.sdTopbarDark || 'assets/images/icons/cn-sapag-logo.png';
        return this.layoutService.isDarkTheme() ? dark : light;
    }

    // Branding textual
    sdTitle: string = 'CN Sapag SIGD';
    sdSubtitle: string = 'CN Sapag Sistema Integral de Gestión Digital';
    sdPais: string = 'Argentina';

    // ——— MENÚ DINÁMICO POR SISTEMA ———
    private buildMenu() {
        const sys = this.systemContext.getSystem?.() || '';
        const user = this.currentUserFull;
        const isSuperAdmin = Number((user as any)?.superadmin) === 1;
        const userClaims = new Set(user?.claims?.map((c: any) => c.nombre) ?? []);
        const hasLVAccess = isSuperAdmin || ['HSE_LISTAS_VERIFICACION_CARGAR','HSE_LISTAS_VERIFICACION_EDITAR','HSE_LISTAS_VERIFICACION_ELIMINAR','HSE_LISTAS_VERIFICACION_APROBAR','HSE_LISTAS_VERIFICACION_ADMIN'].some(c => userClaims.has(c));
        const hasJuegosAccess = isSuperAdmin || ['HSE_JUEGOS','HSE_JUEGOS_PODIO','HSE_JUEGOS_ADMIN'].some(c => userClaims.has(c));
        const hasUsuarios = isSuperAdmin || userClaims.has('SISTEMA_USUARIOS');
        const hasParametros = isSuperAdmin || userClaims.has('SISTEMA_PARAMETROS');

        const base: MenuItem[] = [{ separator: true }];

        if (sys === 'hse') {
            const hseItems: MenuItem[] = [
                { label: 'Gestión Vehicular', icon: 'pi pi-car', routerLink: ['/hse/gestionvehicular/home'], command: () => this.close() },
                { label: 'Tarjetas de Observación', icon: 'pi pi-clipboard', routerLink: ['/hse/tarjetasobs/home'], command: () => this.close() },
                ...(hasLVAccess ? [{ label: 'Checklists', icon: 'pi pi-list-check', routerLink: ['/hse/listasverificacion/home'], command: () => this.close() }] : []),
                ...(hasJuegosAccess ? [{ label: 'Juegos HSE', icon: 'pi pi-objects-column', routerLink: ['/hse/juegos-hse/home'], command: () => this.close() }] : [])
            ];
            base.push({
                label: 'HSE',
                icon: 'pi pi-shield',
                items: hseItems
            });
        }

        if (sys === 'rrhh') {
            base.push({
                label: 'RRHH',
                icon: 'pi pi-users',
                items: [{ label: 'Solicitudes', icon: 'pi pi-calendar-clock', routerLink: ['/rrhh/solicitudes/home'], command: () => this.close() }]
            });
        }

        const ajustesItems: MenuItem[] = [
            ...(hasUsuarios ? [{ label: 'Usuarios', icon: 'pi pi-user', routerLink: ['/usuarios/home'], command: () => this.close() }] : []),
            ...(hasParametros ? [{ label: 'Parámetros', icon: 'pi pi-cog', routerLink: ['/parametros/home'], command: () => this.close() }] : [])
        ];

        if (ajustesItems.length > 0) {
            base.push({ separator: true }, { label: 'Ajustes', icon: 'pi pi-cog', items: ajustesItems });
        }

        base.push(
            { separator: true },
            {
                label: 'Extras',
                icon: 'pi pi-star',
                items: [
                    { label: 'Calendario', icon: 'pi pi-calendar', routerLink: ['/calendario/home'], command: () => this.close() },
                    { label: 'FAQ', icon: 'pi pi-question-circle', routerLink: ['/faq'], command: () => this.close() }
                ]
            }
        );

        base.push(
            { separator: true },
            {
                label: 'Cuenta',
                icon: 'pi pi-user-edit',
                items: [
                    { label: 'Mi Perfil', icon: 'pi pi-user', routerLink: ['/perfil/gestionar-contrasena'], command: () => this.close() },
                    { label: 'Cambiar sistema', icon: 'pi pi-sync', routerLink: ['/selector'], command: () => this.close() },
                    { label: 'Cerrar sesión', icon: 'pi pi-sign-out', command: () => this.logout() }
                ]
            }
        );

        this.items = base;
    }

    // ——— Interacción (cierre seguro) ———
    onItemClick(ev: Event, item: MenuItem) {
        if (item.disabled) {
            ev.preventDefault();
            return;
        }
        if (item.command) item.command({ originalEvent: ev, item });
        // Si no navega, igual cerramos
        if (!item.routerLink) this.close();
        // y si navega, cerramos al siguiente tick
        setTimeout(() => this.close(), 0);
    }

    private isInsideSidebar(target: EventTarget | null): boolean {
        // Solo consideramos "dentro" el panel real del menú (la tarjeta .p-menu),
        // no todo el aside transparente, para que un tap en el área oscura cierre.
        const panel = this.host.nativeElement.querySelector('.mobile-sidebar .p-menu');
        const el = (panel as Element) || this.host.nativeElement.querySelector('.mobile-sidebar');
        return !!(el && (target === el || (target instanceof Node && el.contains(target))));
    }

    private clickIsFromToggle(target: EventTarget | null): boolean {
        const el = target as HTMLElement | null;
        if (!el) return false;
        // Cubre botón, ícono dentro, y cualquier wrapper
        return !!el.closest('[data-sidebar-toggle], .layout-menu-button');
    }

    @HostListener('document:click', ['$event'])
    onDocClick(e: Event) {
        if (!this.mobileSidebar.open()) return;
        if (this.clickIsFromToggle(e.target)) return;
        if (!this.isInsideSidebar(e.target)) this.close();
    }

    @HostListener('document:touchstart', ['$event'])
    onDocTouch(e: Event) {
        if (!this.mobileSidebar.open()) return;
        if (this.clickIsFromToggle(e.target)) return;
        if (!this.isInsideSidebar(e.target)) this.close();
    }

    close() {
        this.mobileSidebar.hide();
    }
    logout() {
        // Cerrar panel y cerrar sesión real usando AuthService
        this.mobileSidebar.hide();
        this.auth.logout();
    }
}
